import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { HeaderBrandSection, TablzLogoLink } from '@/components/headers/desktop-header/HeaderBrandSection';
import { useOutletContext, useLocation } from 'react-router-dom';
import { AvailabilityGuardContext } from '@/routes/AvailabilityGuard';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { TDR } from 'tdr-common';
import { validateAndCorrectLanguage } from '../i18n';
import { DesktopOpenSearchHeader } from '@/components/open-parameters/desktop/DesktopOpenSearchHeader';

type DesktopHeaderProps = {
	restaurantProp?: TDR.Restaurant
}

const HEADER_SECTION_GAP_PX = 32;

function getElementContentRight(element: HTMLElement): number {
	let right = element.getBoundingClientRect().right;
	for (const child of Array.from(element.children)) {
		right = Math.max(right, child.getBoundingClientRect().right);
	}
	return right;
}

function computeSearchLeft(
	headerRect: DOMRect,
	brandRight: number,
	rightRect: DOMRect,
	searchWidth: number
): number {
	const centerX = headerRect.left + headerRect.width / 2;
	let searchLeft = centerX - searchWidth / 2;

	if (searchLeft < brandRight + HEADER_SECTION_GAP_PX) {
		searchLeft = brandRight + HEADER_SECTION_GAP_PX;
	}

	const maxSearchRight = rightRect.left - HEADER_SECTION_GAP_PX;
	if (searchLeft + searchWidth > maxSearchRight) {
		searchLeft = maxSearchRight - searchWidth;
	}

	return searchLeft - headerRect.left;
}

export const DesktopHeader = ({ restaurantProp }: DesktopHeaderProps) => {
	const AvailabilityGuardContext = useOutletContext<AvailabilityGuardContext>();
	const restaurant = restaurantProp || AvailabilityGuardContext?.restaurant;
	const { i18n } = useTranslation();

	const location = useLocation();
	const showsSearch = location.pathname.endsWith('/explore');

	const headerRef = useRef<HTMLDivElement>(null);
	const brandRef = useRef<HTMLDivElement>(null);
	const rightRef = useRef<HTMLDivElement>(null);
	const searchRef = useRef<HTMLDivElement>(null);
	const [searchLeftPx, setSearchLeftPx] = useState<number | null>(null);

	useEffect(() => {
		if (restaurant) {
			validateAndCorrectLanguage(restaurant.supportedLocales);
		}
	}, [restaurant]);

	useLayoutEffect(() => {
		if (!showsSearch) {
			setSearchLeftPx(null);
			return;
		}

		const updateSearchPosition = () => {
			const header = headerRef.current;
			const brand = brandRef.current;
			const right = rightRef.current;
			const search = searchRef.current;
			if (!header || !brand || !right || !search) {
				return;
			}

			setSearchLeftPx(
				computeSearchLeft(
					header.getBoundingClientRect(),
					getElementContentRight(brand),
					right.getBoundingClientRect(),
					search.offsetWidth
				)
			);
		};

		updateSearchPosition();

		const resizeObserver = new ResizeObserver(updateSearchPosition);
		if (headerRef.current) {
			resizeObserver.observe(headerRef.current);
		}
		if (brandRef.current) {
			resizeObserver.observe(brandRef.current);
		}
		if (rightRef.current) {
			resizeObserver.observe(rightRef.current);
		}
		if (searchRef.current) {
			resizeObserver.observe(searchRef.current);
		}

		window.addEventListener('resize', updateSearchPosition);
		return () => {
			resizeObserver.disconnect();
			window.removeEventListener('resize', updateSearchPosition);
		};
	}, [showsSearch, i18n.language, restaurant?.displayName, restaurant?.name]);

	const restaurantInfo = {
		restaurantId: restaurant?.id,
		restaurantName: restaurant?.name,
		restaurantDisplayName: restaurant?.displayName
	};

	return (
		<div
			ref={headerRef}
			className="bg-tablz-dark-gray-2 relative flex w-full items-center justify-between gap-8 overflow-hidden rounded-2xl px-8 py-4"
		>
			<div ref={brandRef} className="min-w-0 shrink">
				<HeaderBrandSection branding={restaurant?.settings?.branding} restaurantInfo={restaurantInfo} />
			</div>
			{showsSearch ? (
				<div
					ref={searchRef}
					className="absolute top-1/2 -translate-y-1/2"
					style={{ left: searchLeftPx ?? '50%', transform: searchLeftPx === null ? 'translate(-50%, -50%)' : 'translateY(-50%)' }}
				>
					<motion.div
						initial={{ opacity: 0, y: -30 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.3 }}
					>
						<DesktopOpenSearchHeader />
					</motion.div>
				</div>
			) : null}
			<div ref={rightRef} className="flex shrink-0 items-center gap-4">
				{restaurant?.settings?.branding?.header?.logoImagePath && (
					<TablzLogoLink restaurantInfo={restaurantInfo} />
				)}
				{(restaurant?.supportedLocales?.length || 0) > 1 && (
					<select
						aria-label="Language"
						className="rounded-md border border-zinc-700 bg-zinc-800 px-2 py-1 text-white"
						value={i18n.language?.startsWith('fr') ? 'fr' : 'en'}
						onChange={(e) => i18n.changeLanguage(e.target.value)}
					>
						{(restaurant?.supportedLocales || ['en']).map((locale) => (
							<option key={locale} value={locale}>
								{locale === 'en' ? 'English' : locale === 'fr' ? 'Français' : locale}
							</option>
						))}
					</select>
				)}
			</div>
		</div>
	);
};
